import { ContractContent } from '../types/contracts';

export interface ContractTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  icon: string;
  content: ContractContent;
}

const DEFAULT_VISIBILITY = 'all' as const;

export const PREBUILT_TEMPLATES: ContractTemplate[] = [
  {
    id: 'wholesale-purchase',
    name: 'Wholesale Purchase Agreement',
    category: 'Acquisitions',
    description: 'Standard wholesale purchase contract for acquiring properties at a discount.',
    icon: '🏠',
    content: {
      fields: [
        { id: 'buyer_name', name: 'Buyer Name', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'seller_name', name: 'Seller Name', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'property_address', name: 'Property Address', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'purchase_price', name: 'Purchase Price', value: '', visibility: DEFAULT_VISIBILITY, type: 'currency' },
        { id: 'earnest_money', name: 'Earnest Money', value: '', visibility: DEFAULT_VISIBILITY, type: 'currency' },
        { id: 'closing_date', name: 'Closing Date', value: '', visibility: DEFAULT_VISIBILITY, type: 'date' },
        { id: 'inspection_period', name: 'Inspection Period (days)', value: '10', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'assignment_fee', name: 'Assignment Fee', value: '', visibility: 'agent_only', type: 'currency' },
        { id: 'additional_terms', name: 'Additional Terms', value: '', visibility: DEFAULT_VISIBILITY, type: 'textarea' },
      ],
      terms: `WHOLESALE PURCHASE AND SALE AGREEMENT

This Agreement is entered into on {{closing_date}} between {{buyer_name}} ("Buyer") and {{seller_name}} ("Seller") for the property located at {{property_address}} (the "Property").

1. PURCHASE PRICE
Buyer agrees to purchase the Property for {{purchase_price}}, payable in cash or certified funds at closing.

2. EARNEST MONEY
Buyer shall deposit {{earnest_money}} as earnest money within 3 business days of execution. This deposit shall be credited toward the purchase price at closing.

3. INSPECTION PERIOD
Buyer shall have {{inspection_period}} business days from the Effective Date to inspect the Property. If unsatisfactory, Buyer may terminate this Agreement and receive a full refund of earnest money.

4. CLOSING DATE
The transaction shall close on or before {{closing_date}}. Time is of the essence.

5. PROPERTY CONDITION — AS-IS
Buyer accepts the Property in its current "AS-IS, WHERE-IS" condition. Seller makes no warranties regarding the condition of the Property.

6. RIGHT TO ASSIGN
Buyer may assign this Agreement without Seller's prior consent. Upon assignment, Buyer is released from further liability.

7. FINANCING
This is a CASH purchase. There is no financing contingency. Buyer shall provide proof of funds within 5 business days of execution.

8. DEFAULT
If Buyer defaults, Seller's sole remedy is to retain the earnest money. If Seller defaults, Buyer may seek specific performance or a refund of the earnest money.

9. GOVERNING LAW
This Agreement shall be governed by the laws of the state where the Property is located.

Additional Terms: {{additional_terms}}

IN WITNESS WHEREOF, the parties execute this Agreement on the date first written above.

Seller: ___________________________     Date: _______________
{{seller_name}}

Buyer: ____________________________     Date: _______________
{{buyer_name}}`
    }
  },
  {
    id: 'assignment-contract',
    name: 'Assignment of Contract',
    category: 'Dispositions',
    description: 'Assign your equitable interest in a purchase contract to an end buyer.',
    icon: '📋',
    content: {
      fields: [
        { id: 'assignor_name', name: 'Assignor (Your Company)', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'assignee_name', name: 'Assignee (End Buyer)', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'property_address', name: 'Property Address', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'original_purchase_price', name: 'Original Purchase Price', value: '', visibility: DEFAULT_VISIBILITY, type: 'currency' },
        { id: 'assignment_fee', name: 'Assignment Fee', value: '', visibility: DEFAULT_VISIBILITY, type: 'currency' },
        { id: 'deposit_due', name: 'Deposit Due at Signing', value: '', visibility: DEFAULT_VISIBILITY, type: 'currency' },
        { id: 'closing_date', name: 'Closing Date', value: '', visibility: DEFAULT_VISIBILITY, type: 'date' },
        { id: 'original_contract_date', name: 'Original Contract Date', value: '', visibility: DEFAULT_VISIBILITY, type: 'date' },
      ],
      terms: `ASSIGNMENT OF REAL ESTATE PURCHASE CONTRACT

This Assignment is entered into on the date last signed below.

ASSIGNOR: {{assignor_name}} (Original Buyer under the Purchase Agreement)
ASSIGNEE: {{assignee_name}}

1. ASSIGNMENT
Assignor hereby assigns all rights, title, and interest in the Purchase and Sale Agreement dated {{original_contract_date}} for the property located at {{property_address}} to Assignee.

2. ASSIGNMENT FEE
In consideration of this Assignment, Assignee agrees to pay Assignor a non-refundable assignment fee of {{assignment_fee}}, payable as follows:
- {{deposit_due}} due upon execution of this Assignment (non-refundable deposit)
- The balance due at Closing

3. ASSUMPTION OF OBLIGATIONS
Assignee accepts and assumes all obligations under the Original Agreement, including the obligation to close at the purchase price of {{original_purchase_price}} on or before {{closing_date}}.

4. ORIGINAL AGREEMENT
The Original Agreement remains in full force. In case of conflict, the Original Agreement prevails.

5. REPRESENTATIONS
Assignor represents that: (a) the Original Agreement is in full force; (b) Assignor is not in default; (c) Assignor has authority to assign.

6. CLOSING
Assignee shall close the transaction by {{closing_date}}. Failure to close constitutes default.

AGREED AND ACCEPTED:

Assignor: ____________________________     Date: _______________
{{assignor_name}}

Assignee: ____________________________     Date: _______________
{{assignee_name}}`
    }
  },
  {
    id: 'fix-flip-contract',
    name: 'Fix & Flip Purchase Agreement',
    category: 'Acquisitions',
    description: 'Purchase contract tailored for fix-and-flip investors with rehab scope.',
    icon: '🔨',
    content: {
      fields: [
        { id: 'buyer_name', name: 'Buyer Name', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'seller_name', name: 'Seller Name', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'property_address', name: 'Property Address', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'purchase_price', name: 'Purchase Price', value: '', visibility: DEFAULT_VISIBILITY, type: 'currency' },
        { id: 'arv', name: 'After Repair Value (ARV)', value: '', visibility: 'agent_only', type: 'currency' },
        { id: 'rehab_budget', name: 'Estimated Rehab Budget', value: '', visibility: 'agent_only', type: 'currency' },
        { id: 'earnest_money', name: 'Earnest Money', value: '', visibility: DEFAULT_VISIBILITY, type: 'currency' },
        { id: 'closing_date', name: 'Closing Date', value: '', visibility: DEFAULT_VISIBILITY, type: 'date' },
        { id: 'due_diligence_days', name: 'Due Diligence Period (days)', value: '14', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'rehab_scope', name: 'Scope of Repairs (brief)', value: '', visibility: DEFAULT_VISIBILITY, type: 'textarea' },
      ],
      terms: `FIX AND FLIP PURCHASE AGREEMENT

This Agreement is entered into between {{buyer_name}} ("Buyer/Investor") and {{seller_name}} ("Seller") for the property located at {{property_address}}.

1. PURCHASE PRICE: {{purchase_price}}

2. EARNEST MONEY: {{earnest_money}} deposited within 3 business days. Applied to purchase price at closing.

3. DUE DILIGENCE PERIOD
Buyer shall have {{due_diligence_days}} calendar days to conduct inspections, contractor assessments, and feasibility analysis. Buyer may terminate for any reason during this period and receive a full refund of earnest money.

4. AS-IS PURCHASE
Buyer accepts the Property in its current AS-IS condition. Seller makes no warranties regarding condition, structural integrity, mechanical systems, or compliance with codes.

Anticipated Scope of Repairs:
{{rehab_scope}}

5. CASH PURCHASE
This is a cash purchase. No financing contingency. Buyer shall provide Proof of Funds within 5 business days.

6. CLOSING DATE: {{closing_date}}. Time is of the essence.

7. RIGHT TO ASSIGN: Buyer may assign this Agreement without prior consent.

8. TITLE: Seller shall convey marketable title by warranty deed, free and clear of all liens.

9. CLOSING COSTS: Each party pays their own attorney fees. Seller pays deed stamps. Buyer pays recording fees and title insurance.

IN WITNESS WHEREOF:

Seller: ____________________________     Date: _______________
{{seller_name}}

Buyer/Investor: ____________________     Date: _______________
{{buyer_name}}`
    }
  },
  {
    id: 'lease-agreement',
    name: 'Residential Lease Agreement',
    category: 'Rentals',
    description: 'Standard month-to-month or fixed-term residential lease.',
    icon: '🏘️',
    content: {
      fields: [
        { id: 'landlord_name', name: 'Landlord Name', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'tenant_name', name: 'Tenant Name', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'property_address', name: 'Property Address', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'monthly_rent', name: 'Monthly Rent', value: '', visibility: DEFAULT_VISIBILITY, type: 'currency' },
        { id: 'security_deposit', name: 'Security Deposit', value: '', visibility: DEFAULT_VISIBILITY, type: 'currency' },
        { id: 'lease_start', name: 'Lease Start Date', value: '', visibility: DEFAULT_VISIBILITY, type: 'date' },
        { id: 'lease_end', name: 'Lease End Date', value: '', visibility: DEFAULT_VISIBILITY, type: 'date' },
        { id: 'rent_due_day', name: 'Rent Due Day of Month', value: '1st', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'late_fee', name: 'Late Fee (after 5 days)', value: '', visibility: DEFAULT_VISIBILITY, type: 'currency' },
        { id: 'pets_allowed', name: 'Pets Allowed?', value: 'No', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'utilities', name: 'Utilities Responsibility', value: 'Tenant pays all utilities', visibility: DEFAULT_VISIBILITY, type: 'text' },
      ],
      terms: `RESIDENTIAL LEASE AGREEMENT

This Lease Agreement is entered into between {{landlord_name}} ("Landlord") and {{tenant_name}} ("Tenant") for the property located at {{property_address}}.

1. TERM
Lease begins {{lease_start}} and ends {{lease_end}}, unless terminated earlier per this Agreement.

2. RENT
Monthly rent: {{monthly_rent}}, due on the {{rent_due_day}} of each month. A late fee of {{late_fee}} will be assessed after 5 days.

3. SECURITY DEPOSIT
Tenant shall pay {{security_deposit}} as a security deposit, to be held by Landlord and returned within 30 days of move-out, less any deductions for damages beyond normal wear and tear.

4. UTILITIES
{{utilities}}

5. OCCUPANCY
Property shall be occupied solely by the named Tenant(s). No subletting without Landlord's written consent.

6. PETS
Pets: {{pets_allowed}}

7. MAINTENANCE
Tenant shall maintain the Property in clean, sanitary condition. Tenant is responsible for minor repairs under $100. Major repairs are Landlord's responsibility.

8. ENTRY
Landlord shall provide 24-hour notice before entering, except in emergencies.

9. TERMINATION
Either party may terminate with 30 days written notice. Tenant remains liable for rent through the end of the notice period.

10. GOVERNING LAW
This Lease is governed by the laws of the state where the Property is located.

Landlord: ___________________________     Date: _______________
{{landlord_name}}

Tenant: _____________________________     Date: _______________
{{tenant_name}}`
    }
  },
  {
    id: 'property-management',
    name: 'Property Management Agreement',
    category: 'Management',
    description: 'Agreement between property owner and management company.',
    icon: '🏢',
    content: {
      fields: [
        { id: 'owner_name', name: 'Property Owner Name', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'manager_name', name: 'Property Manager / Company', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'property_address', name: 'Property Address', value: '', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'management_fee', name: 'Monthly Management Fee (%)', value: '10%', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'leasing_fee', name: 'Leasing / Placement Fee', value: '1 month\'s rent', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'agreement_start', name: 'Agreement Start Date', value: '', visibility: DEFAULT_VISIBILITY, type: 'date' },
        { id: 'maintenance_limit', name: 'Maintenance Authorization Limit', value: '$500', visibility: DEFAULT_VISIBILITY, type: 'text' },
        { id: 'notice_period', name: 'Termination Notice Period (days)', value: '30', visibility: DEFAULT_VISIBILITY, type: 'text' },
      ],
      terms: `PROPERTY MANAGEMENT AGREEMENT

This Agreement is entered into between {{owner_name}} ("Owner") and {{manager_name}} ("Manager") for the management of the property located at {{property_address}}, effective {{agreement_start}}.

1. APPOINTMENT
Owner appoints Manager as the exclusive property manager for the Property.

2. MANAGEMENT FEE
Owner agrees to pay Manager {{management_fee}} of the monthly gross rental income collected as a management fee. This fee is due monthly.

3. LEASING FEE
For each new tenant placement, Owner agrees to pay a leasing fee of {{leasing_fee}}.

4. MANAGER AUTHORITY
Manager is authorized to:
- Advertise the Property for rent
- Screen and select qualified tenants
- Collect rent and security deposits
- Make repairs and maintenance up to {{maintenance_limit}} without Owner approval
- Handle tenant communications and complaints

5. ACCOUNTING
Manager shall provide Owner with a monthly accounting statement by the 15th of each month.

6. OWNER RESPONSIBILITIES
Owner shall maintain insurance on the Property and ensure it meets all applicable codes and habitability standards.

7. TERMINATION
Either party may terminate this Agreement with {{notice_period}} days written notice. Manager shall transition all records and funds within 30 days.

8. INDEMNIFICATION
Owner shall indemnify Manager against all claims arising from Owner's actions or the Property's condition, except where caused by Manager's negligence.

Owner: _____________________________     Date: _______________
{{owner_name}}

Manager: ____________________________     Date: _______________
{{manager_name}}`
    }
  }
];

// Custom templates stored in localStorage
const CUSTOM_TEMPLATES_KEY = 'wholescale-contract-templates';

export function getCustomTemplates(): ContractTemplate[] {
  try {
    const raw = localStorage.getItem(CUSTOM_TEMPLATES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveCustomTemplate(template: ContractTemplate): void {
  const existing = getCustomTemplates();
  const idx = existing.findIndex(t => t.id === template.id);
  if (idx >= 0) {
    existing[idx] = template;
  } else {
    existing.push(template);
  }
  localStorage.setItem(CUSTOM_TEMPLATES_KEY, JSON.stringify(existing));
}

export function deleteCustomTemplate(id: string): void {
  const existing = getCustomTemplates();
  localStorage.setItem(CUSTOM_TEMPLATES_KEY, JSON.stringify(existing.filter(t => t.id !== id)));
}

export function getAllTemplates(): ContractTemplate[] {
  return [...PREBUILT_TEMPLATES, ...getCustomTemplates()];
}
